import { MdLink } from "@react-icons/all-files/md/MdLink";
import { MdCode } from "@react-icons/all-files/md/MdCode";
import {
  BubbleMenu,
  type Editor,
} from "@tiptap/react";
import clsx from "clsx";
import type { FC } from "react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { MdFormatBold } from "@react-icons/all-files/md/MdFormatBold";
import { MdFormatStrikethrough } from "@react-icons/all-files/md/MdFormatStrikethrough";
import { MdFormatQuote } from "@react-icons/all-files/md/MdFormatQuote";
import { MdFormatItalic } from "@react-icons/all-files/md/MdFormatItalic";
import { IoMdClose } from "@react-icons/all-files/io/IoMdClose";
import { numToArray } from "tests/lib/util";
interface AppBubbleMenuProps {
  editor: Editor;
  setContent: () => void;
}

const AppBubbleMenu: FC<AppBubbleMenuProps> = ({
  editor,
  setContent,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const onCLick = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const [linkUrl, setLinkUrl] = useState("");
  const setLink = useCallback(() => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .setLink({ href: linkUrl })
        .run();
    }
    setShowLinkInput(false);
    setLinkUrl("");
  }, [editor, linkUrl]);

  const [showLinkInput, setShowLinkInput] =
    useState(false);
  useEffect(() => {
    if (menuOpen) {
      window.addEventListener("click", onCLick);
      return () => {
        window.removeEventListener(
          "click",
          onCLick,
        );
      };
    }
  }, [menuOpen, onCLick]);

  const activeHead = () => {
    const fil = numToArray(4).filter((el) => {
      return editor.isActive("heading", {
        level: el + 1,
      });
    });

    if (fil.length) {
      return fil[0] + 1;
    }
    return -1;
  };
  const [isVisible, setIsVisible] =
    useState(false);
  const resetStates = useCallback(() => {
    setLinkUrl("");
    setShowLinkInput(false);
    setMenuOpen(false);
  }, []);
  useEffect(() => {
    if (!isVisible) {
      resetStates();
    }
  }, [isVisible, resetStates]);
  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      className="bubble-menu"
      tippyOptions={{ duration: 100 }}
      editor={editor}
      shouldShow={({
        editor,
        view,
        state,
        oldState,
        from,
        to,
      }) => {
        const shouldDisplay = from !== to;
        setIsVisible(shouldDisplay);
        return shouldDisplay;
      }}
    >
      <div
        className={clsx(
          "flex",
          "items-center",
          "gap-12",
          "text-24",
          "border",
          "px-15",
          "py-5",
          "bg-white",
        )}
      >
        <div
          className={clsx(
            "relative",
            "text-14",
            "font-bold",
          )}
        >
          <button
            onClick={(e) => {
              setMenuOpen(true);
              e.stopPropagation();
            }}
            className={clsx(
              "inline-flex border",
              "border-gray-100",
              "px-16",
              "py-4",
              "min-w-128",
            )}
            type="button"
          >
            {activeHead() === -1
              ? "文章"
              : `見出しh${activeHead()}`}
          </button>
          {menuOpen && (
            <div
              className={clsx(
                "z-10 w-176 divide-y divide-gray-100",
                "bg-[#fff]",
                "absolute",
                "left-0",
                "top-0",
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ul
                className={clsx(
                  "text-sm py-8 text-gray-700",
                  "divide-y",
                  "border-gray-100",
                  "border",
                )}
                aria-labelledby="dropdownDefaultButton"
              >
                <li
                  key={"para"}
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .setParagraph()
                      .run();
                    setMenuOpen(false);
                  }}
                  className={clsx(
                    "block cursor-pointer px-16 py-8 hover:bg-gray-100",
                  )}
                >
                  文章
                </li>

                {numToArray(4).map((el) => {
                  return (
                    <li
                      key={el}
                      onClick={() => {
                        editor
                          .chain()
                          .focus()
                          .toggleHeading({
                            level: (el +
                              1) as any,
                          })
                          .run();
                        setMenuOpen(false);
                      }}
                      className={clsx(
                        "block cursor-pointer px-16 py-8 hover:bg-gray-100",
                      )}
                    >
                      見出しh{el + 1}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          }
          className={clsx(
            !editor.isActive("bold") &&
              "opacity-50",
          )}
        >
          <MdFormatBold />
        </button>
        <button
          type="button"
          className={clsx(
            !editor.isActive("italic") &&
              "opacity-50",
          )}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          }
        >
          <MdFormatItalic />
        </button>

        <button
          type="button"
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleStrike()
              .run()
          }
          className={clsx(
            !editor.isActive("strike") &&
              "opacity-50",
          )}
        >
          <MdFormatStrikethrough />
        </button>

        <button
          type="button"
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleCodeBlock()
              .run()
          }
          className={clsx(
            !editor.isActive("code_block") &&
              "opacity-50",
          )}
        >
          <MdCode />
        </button>
        <button
          type="button"
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBlockquote()
              .run()
          }
          className={clsx(
            !editor.isActive("blockquote") &&
              "opacity-50",
          )}
        >
          <MdFormatQuote />
        </button>

        <div className={clsx("relative")}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => {
                setShowLinkInput((prev) => {
                  if (!prev) {
                    //今の入力があれば反映
                    setLinkUrl(
                      editor.getAttributes("link")
                        .href || "",
                    );
                  }
                  return !prev;
                });
              }}
              className={clsx(
                !editor.isActive("link") &&
                  "opacity-50",
              )}
            >
              <MdLink />
            </button>
          </div>
          {showLinkInput && (
            <div
              className={clsx(
                "flex",
                "items-center",
                "absolute",
                "bottom-[110%]",
                "left-0",
                "border",
                "border-gray-100",
                "bg-white",
                "items-center",
              )}
            >
              <div
                className={clsx(
                  "cursor-pointer",
                  "text-gray-500",
                  "px-5",
                )}
                onClick={() => {
                  setShowLinkInput(false);
                  setLinkUrl("");
                }}
              >
                <IoMdClose
                  className={clsx("w-20", "h-15")}
                ></IoMdClose>
              </div>
              <input
                type="url"
                value={linkUrl}
                placeholder="URLを入力"
                className={clsx(
                  "text-14",
                  "px-5",
                  "py-5",
                  "rounded-none",
                )}
                onChange={(e) =>
                  setLinkUrl(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setLink();
                  }
                  if (e.key === "Escape") {
                    setShowLinkInput(false);
                    setLinkUrl("");
                  }
                }}
              />
              <button
                onClick={setLink}
                className={clsx(
                  "text-12",
                  "text-gray-500",
                  "bg-gray-500",
                  "text-white",
                  "whitespace-nowrap",
                  "px-10",
                  "py-7",
                )}
              >
                追加
              </button>
            </div>
          )}
        </div>
      </div>
    </BubbleMenu>
  );
};

export default AppBubbleMenu;
